import React, { useState, useEffect } from 'react'
import { LoginForm } from './components/auth/LoginForm'
import { RegisterForm } from './components/auth/RegisterForm'
import { LandingPage } from './pages/LandingPage'
import { ChildDisplay } from './components/child/ChildDisplay'
import { InventoryPanel } from './components/inventory/InventoryPanel'
import { GuildPanel } from './components/social/GuildPanel'
import { KingdomView } from './components/kingdom/KingdomView'
import SkillTreePage from './pages/progression/SkillTreePage'
import AchievementPage from './pages/progression/AchievementPage'
import LeaderboardPage from './pages/progression/LeaderboardPage'
import EventCalendarPage from './pages/EventCalendarPage'
import QuestTrackerPage from './pages/QuestTrackerPage'
import SeasonalShopPage from './pages/SeasonalShopPage'
import { FeaturesPage } from './pages/FeaturesPage'
import { PricingPage } from './pages/PricingPage'
import { BlogPage } from './pages/BlogPage'
import { HelpPage } from './pages/HelpPage'
import { CareersPage } from './pages/CareersPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { TermsPage } from './pages/TermsPage'
import { AboutModal } from './modals/AboutModal'
import { ContactModal } from './modals/ContactModal'
import { ParentsGuideModal } from './modals/ParentsGuideModal'
import { SecurityModal } from './modals/SecurityModal'
import { CookiesModal } from './modals/CookiesModal'
import { useChild } from './hooks/useChild'
import { authService } from './services/auth'
import { useNavigation } from './contexts/NavigationContext'

type GameTab = 'children' | 'inventory' | 'guilds' | 'kingdom' | 'progression' | 'leaderboard' | 'events' | 'quests' | 'shop'

export function App(): React.ReactElement {
  const { currentPage, setCurrentPage, activeModal, openModal, closeModal } = useNavigation()
  const [currentTab, setCurrentTab] = useState<GameTab>('children')
  const [progressionTab, setProgressionTab] = useState<'skills' | 'achievements'>('skills')
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null)
  const [isRegistering, setIsRegistering] = useState(false)

  const { children, child, fetchChildren, fetchChild, createChild, performAction } = useChild()

  console.log('App rendering with currentPage:', currentPage)

  useEffect(() => {
    if (authService.isAuthenticated()) {
      setCurrentPage('game')
      fetchChildren()
    }
  }, [])

  const handleAuthSuccess = () => {
    setCurrentPage('game')
    fetchChildren()
  }

  const handleGoToLogin = () => {
    setIsRegistering(false)
    setCurrentPage('auth')
  }

  const handleGoToRegister = () => {
    setIsRegistering(true)
    setCurrentPage('register')
  }

  const handleGetStarted = () => {
    setCurrentPage('auth')
  }

  const handleCreateChild = async () => {
    const name = prompt('Enter child name:')
    if (name) {
      try {
        const newChild = await createChild(name)
        setSelectedChildId(newChild.id)
        fetchChildren()
        alert(`${name} has been created!`)
      } catch (error) {
        alert('Failed to create child')
      }
    }
  }

  const handleChildAction = async (action: string) => {
    if (selectedChildId) {
      try {
        await performAction(selectedChildId, action)
        fetchChild(selectedChildId)
      } catch (error) {
        console.error('Action failed:', error)
      }
    }
  }

  const handleLogout = () => {
    authService.logout()
    setCurrentPage('auth')
    setSelectedChildId(null)
  }

  // Landing Page
  if (currentPage === 'landing') {
    return <LandingPage onGetStarted={handleGetStarted} />
  }

  // Auth Pages
  if (currentPage === 'auth') {
    return <LoginForm onSuccess={handleAuthSuccess} onRegisterClick={handleGoToRegister} />
  }

  if (currentPage === 'register') {
    return <RegisterForm onSuccess={handleAuthSuccess} onLoginClick={handleGoToLogin} />
  }

  // Footer Pages
  if (currentPage === 'features') {
    return (
      <>
        <FeaturesPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'pricing') {
    return (
      <>
        <PricingPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'blog') {
    return (
      <>
        <BlogPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'help') {
    return (
      <>
        <HelpPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'careers') {
    return (
      <>
        <CareersPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'privacy') {
    return (
      <>
        <PrivacyPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  if (currentPage === 'terms') {
    return (
      <>
        <TermsPage />
        {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
        {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
      </>
    )
  }

  // Modal-only pages (no backing page component, just display the modal)
  if (currentPage === 'about') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
          <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-purple-700 font-bold text-lg hover:text-pink-600 flex items-center gap-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-black text-purple-700">About</h1>
              <div className="w-8"></div>
            </div>
          </nav>
          <div className="flex items-center justify-center py-12">
            <AboutModal isOpen={true} onClose={() => setCurrentPage('landing')} />
          </div>
        </div>
      </>
    )
  }

  if (currentPage === 'contact') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
          <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-purple-700 font-bold text-lg hover:text-pink-600 flex items-center gap-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-black text-purple-700">Contact</h1>
              <div className="w-8"></div>
            </div>
          </nav>
          <div className="flex items-center justify-center py-12">
            <ContactModal isOpen={true} onClose={() => setCurrentPage('landing')} />
          </div>
        </div>
      </>
    )
  }

  if (currentPage === 'parentsGuide') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
          <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-purple-700 font-bold text-lg hover:text-pink-600 flex items-center gap-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-black text-purple-700">Parents Guide</h1>
              <div className="w-8"></div>
            </div>
          </nav>
          <div className="flex items-center justify-center py-12">
            <ParentsGuideModal isOpen={true} onClose={() => setCurrentPage('landing')} />
          </div>
        </div>
      </>
    )
  }

  if (currentPage === 'security') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
          <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-purple-700 font-bold text-lg hover:text-pink-600 flex items-center gap-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-black text-purple-700">Security</h1>
              <div className="w-8"></div>
            </div>
          </nav>
          <div className="flex items-center justify-center py-12">
            <SecurityModal isOpen={true} onClose={() => setCurrentPage('landing')} />
          </div>
        </div>
      </>
    )
  }

  if (currentPage === 'cookies') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-blue-50">
          <nav className="sticky top-0 z-50 bg-white border-b-4 border-purple-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-purple-700 font-bold text-lg hover:text-pink-600 flex items-center gap-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-black text-purple-700">Cookies</h1>
              <div className="w-8"></div>
            </div>
          </nav>
          <div className="flex items-center justify-center py-12">
            <CookiesModal isOpen={true} onClose={() => setCurrentPage('landing')} />
          </div>
        </div>
      </>
    )
  }

  // Game Page
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 shadow-lg">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <h1 className="text-3xl font-bold">👶 BabyKingdom</h1>
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg font-semibold"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b-2 border-purple-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto flex gap-2 p-2 overflow-x-auto">
            <button
              onClick={() => setCurrentTab('children')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'children'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              👶 Children
            </button>
            <button
              onClick={() => setCurrentTab('inventory')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'inventory'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🎒 Inventory
            </button>
            <button
              onClick={() => setCurrentTab('progression')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'progression'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⭐ Progression
            </button>
            <button
              onClick={() => setCurrentTab('leaderboard')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'leaderboard'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🏆 Leaderboard
            </button>
            <button
              onClick={() => setCurrentTab('guilds')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'guilds'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⚔️ Guilds
            </button>
            <button
              onClick={() => setCurrentTab('kingdom')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'kingdom'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🏰 Kingdom
            </button>
            <button
              onClick={() => setCurrentTab('events')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'events'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📅 Events
            </button>
            <button
              onClick={() => setCurrentTab('quests')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'quests'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🎯 Quests
            </button>
            <button
              onClick={() => setCurrentTab('shop')}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                currentTab === 'shop'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🛍️ Shop
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto p-4">
          {currentTab === 'children' && (
            <div className="space-y-4">
              <button
                onClick={handleCreateChild}
                className="w-full bg-green-500 text-white py-3 rounded-lg font-bold text-lg hover:bg-green-600"
              >
                ➕ Create New Child
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Children List */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow-lg p-4">
                  <h3 className="text-lg font-bold mb-3 text-gray-800">My Children</h3>
                  <div className="space-y-2">
                    {children.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setSelectedChildId(c.id)
                          fetchChild(c.id)
                        }}
                        className={`w-full text-left p-3 rounded-lg font-semibold transition ${
                          selectedChildId === c.id
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        👶 {c.name}
                        <p className="text-sm font-normal">{c.stage}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Child Display */}
                {child && selectedChildId && (
                  <div className="lg:col-span-2">
                    <ChildDisplay child={child} onAction={handleChildAction} />
                  </div>
                )}
              </div>
            </div>
          )}

          {currentTab === 'inventory' && selectedChildId && (
            <InventoryPanel childId={selectedChildId} />
          )}

          {currentTab === 'progression' && selectedChildId && (
            <div>
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => setProgressionTab('skills')}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    progressionTab === 'skills'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-400'
                  }`}
                >
                  ⭐ Skills
                </button>
                <button
                  onClick={() => setProgressionTab('achievements')}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    progressionTab === 'achievements'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-400'
                  }`}
                >
                  🏆 Achievements
                </button>
              </div>
              {progressionTab === 'skills' && <SkillTreePage />}
              {progressionTab === 'achievements' && <AchievementPage />}
            </div>
          )}

          {currentTab === 'leaderboard' && selectedChildId && (
            <LeaderboardPage />
          )}

          {currentTab === 'guilds' && <GuildPanel />}

          {currentTab === 'kingdom' && <KingdomView />}

          {currentTab === 'events' && selectedChildId && (
            <EventCalendarPage />
          )}

          {currentTab === 'quests' && selectedChildId && (
            <QuestTrackerPage />
          )}

          {currentTab === 'shop' && selectedChildId && (
            <SeasonalShopPage />
          )}
        </div>
      </div>

      {/* Modals */}
      {activeModal === 'about' && <AboutModal isOpen={true} onClose={closeModal} />}
      {activeModal === 'contact' && <ContactModal isOpen={true} onClose={closeModal} />}
      {activeModal === 'parentsGuide' && <ParentsGuideModal isOpen={true} onClose={closeModal} />}
      {activeModal === 'security' && <SecurityModal isOpen={true} onClose={closeModal} />}
      {activeModal === 'cookies' && <CookiesModal isOpen={true} onClose={closeModal} />}
    </>
  )
}

export default App
